import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Check,
  GitBranch,
  Plus,
  Trash2,
  Settings2,
  Link,
  Undo2,
  Unlink,
} from 'lucide-react';

import { Button } from '@/components/ui/button';

import type { WorkflowMutationEntry } from './types';

export interface WorkflowMutationMessageProps {
  mutations: WorkflowMutationEntry[];
  applied: boolean;
  onApply: () => void;
  // When true, the workflow is locked and Apply is disabled. See the
  // matching prop on NodeUpdateMessage for the full rationale.
  applyDisabled?: boolean;
  applyDisabledReason?: string;
  undone?: boolean;
  // Absent where the surface can't undo an apply; the card then shows only
  // the Applied state.
  onUndo?: () => void;
}

const EVENT_ICON: Record<string, React.ReactNode> = {
  add_node: <Plus className='size-3 text-emerald-500' />,
  remove_node: <Trash2 className='size-3 text-red-500' />,
  configure_node: <Settings2 className='size-3 text-blue-500' />,
  connect_nodes: <Link className='size-3 text-violet-500' />,
  disconnect_nodes: <Unlink className='size-3 text-orange-500' />,
};

function formatFieldName(fieldId: string): string {
  return fieldId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const PREVIEW_LENGTH = 80;

const ConfigureDetail: React.FC<{
  updates: Array<{ field_id: string; value: string }>;
}> = ({ updates }) => (
  <ul className='ml-5 mt-0.5 space-y-0.5'>
    {updates.map((u) => {
      const display =
        typeof u.value === 'string' && u.value.length > PREVIEW_LENGTH
          ? u.value.slice(0, PREVIEW_LENGTH) + '…'
          : String(u.value);
      return (
        <li
          key={u.field_id}
          className='text-[11px] text-zinc-500 dark:text-zinc-500'
        >
          <span className='font-medium text-zinc-600 dark:text-zinc-400'>
            {formatFieldName(u.field_id)}
          </span>
          <span className='text-zinc-400 dark:text-zinc-600'>{' → '}</span>
          <span className='whitespace-pre-wrap break-words'>{display}</span>
        </li>
      );
    })}
  </ul>
);

const MutationRow: React.FC<{ mutation: WorkflowMutationEntry }> = ({
  mutation,
}) => {
  const eventType = mutation.eventData.event_type as string;
  const isConfigureNode = eventType === 'configure_node';
  const updates = mutation.eventData.updates as
    | Array<{ field_id: string; value: string }>
    | undefined;
  const hasDetails = isConfigureNode && updates && updates.length > 0;

  const [detailOpen, setDetailOpen] = useState(false);
  const detailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (detailOpen && detailRef.current) {
      setTimeout(() => {
        detailRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 50);
    }
  }, [detailOpen]);

  return (
    <li>
      <button
        type='button'
        disabled={!hasDetails}
        onClick={() => hasDetails && setDetailOpen((v) => !v)}
        className={`flex items-start gap-1.5 text-left text-xs text-zinc-600 dark:text-zinc-400 ${hasDetails ? 'cursor-pointer hover:text-zinc-900 dark:hover:text-zinc-200' : ''}`}
      >
        <span className='mt-0.5 shrink-0'>
          {EVENT_ICON[eventType] ?? (
            <GitBranch className='size-3 text-zinc-400' />
          )}
        </span>
        <span className='min-w-0 break-words'>{mutation.label}</span>
        {hasDetails &&
          (detailOpen ? (
            <ChevronDown className='size-3 text-zinc-400' />
          ) : (
            <ChevronRight className='size-3 text-zinc-400' />
          ))}
      </button>
      {detailOpen && updates && (
        <div ref={detailRef}>
          <ConfigureDetail updates={updates} />
        </div>
      )}
    </li>
  );
};

export const WorkflowMutationMessage: React.FC<
  WorkflowMutationMessageProps
> = ({
  mutations,
  applied,
  onApply,
  applyDisabled = false,
  applyDisabledReason,
  undone = false,
  onUndo,
}) => {
  const [expanded, setExpanded] = useState(false);
  const detailListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expanded && detailListRef.current) {
      setTimeout(() => {
        detailListRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 50);
    }
  }, [expanded]);

  const count = mutations.length;
  const summary = `${count} workflow change${count !== 1 ? 's' : ''}`;

  return (
    <div className='w-full rounded-xl border border-border bg-zinc-50 dark:bg-zinc-900'>
      {/* Header row: toggle + summary + apply */}
      <div className='flex items-center gap-2 px-3 py-2'>
        <button
          type='button'
          onClick={() => setExpanded((v) => !v)}
          className='flex min-w-0 flex-1 items-center gap-1.5 text-xs font-medium text-zinc-600 transition-colors hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200'
        >
          {expanded ? (
            <ChevronDown className='size-3.5 shrink-0' />
          ) : (
            <ChevronRight className='size-3.5 shrink-0' />
          )}
          <GitBranch className='size-3.5 shrink-0' />
          <span className='truncate'>{summary}</span>
        </button>

        {undone && (
          <span className='shrink-0 whitespace-nowrap text-xs text-muted-foreground'>
            Undone
          </span>
        )}
        {applied && (
          <span className='flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-medium text-emerald-600 dark:text-emerald-400'>
            <Check className='size-3.5' />
            Applied
          </span>
        )}
        {applied && onUndo && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={onUndo}
            disabled={applyDisabled}
            title={applyDisabled ? applyDisabledReason : undefined}
            className='h-6 shrink-0 gap-1 px-2 text-xs'
          >
            <Undo2 className='size-3.5' />
            Undo
          </Button>
        )}
        {!applied && (
          <button
            type='button'
            onClick={onApply}
            disabled={applyDisabled}
            title={applyDisabled ? applyDisabledReason : undefined}
            className='shrink-0 whitespace-nowrap rounded-lg bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-primary'
          >
            {undone ? 'Reapply' : 'Apply changes'}
          </button>
        )}
      </div>

      {/* Expanded detail list */}
      {expanded && (
        <div
          ref={detailListRef}
          className='border-t border-border px-3 pb-2 pt-1.5'
        >
          <ul className='space-y-1'>
            {mutations.map((m, i) => (
              <MutationRow key={i} mutation={m} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
